import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ClipboardList, ChevronDown, ChevronUp, Plus, RefreshCw, Trash2 } from "lucide-react";

// Default commercial considerations
const defaultConsiderations = [
  "Los precios son en dólares americanos (USD)",
  "Los precios no incluyen IGV",
  "Validez de cotización: 30 días calendario",
  "Tiempo de entrega: 45-60 días hábiles después de la aprobación",
  "Forma de pago: 50% adelanto, 50% contra entrega",
  "El vidrio será templado de 6mm según especificación",
  "Garantía de 5 años en perfiles y 2 años en accesorios",
  "Los trabajos de albañilería no están incluidos"
];

const CommercialConsiderationsSection = ({
  isOpen,
  onOpenChange,
  considerations,
  setConsiderations,
  newConsideration,
  setNewConsideration
}) => {
  const handleAddConsideration = () => {
    if (newConsideration.trim()) {
      setConsiderations([...considerations, newConsideration.trim()]);
      setNewConsideration("");
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && newConsideration.trim()) {
      handleAddConsideration();
    }
  };

  const handleRemove = (idx) => {
    setConsiderations(considerations.filter((_, i) => i !== idx));
  };

  const handleUpdate = (idx, value) => {
    const updated = [...considerations];
    updated[idx] = value;
    setConsiderations(updated);
  };

  return (
    <Collapsible open={isOpen} onOpenChange={onOpenChange}>
      <Card className="border-amber-200">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-amber-50 transition-colors py-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <ClipboardList className="w-4 h-4 text-amber-600" />
                Consideraciones Comerciales
                <span className="bg-amber-100 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                  {considerations.length}
                </span>
              </CardTitle>
              {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </div>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="pt-0 space-y-3">
            <p className="text-xs text-slate-500">
              Estas condiciones aparecerán en la sección de consideraciones del PDF impreso
            </p>
            
            {/* List of considerations */}
            <div className="space-y-1.5">
              {considerations.map((consideration, idx) => (
                <div 
                  key={`consideration-${idx}`}
                  className="flex items-start gap-2 bg-slate-50 rounded-lg p-2 group"
                >
                  <span className="text-amber-500 font-bold text-sm mt-0.5">{idx + 1}.</span>
                  <Input
                    value={consideration}
                    onChange={(e) => handleUpdate(idx, e.target.value)}
                    className="flex-1 h-8 text-sm bg-white"
                  />
                  <button
                    onClick={() => handleRemove(idx)}
                    className="p-1.5 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Eliminar"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add new consideration */}
            <div className="flex items-center gap-2 pt-2 border-t">
              <Input
                value={newConsideration}
                onChange={(e) => setNewConsideration(e.target.value)}
                placeholder="Nueva consideración..."
                className="flex-1 h-9"
                onKeyDown={handleKeyDown}
              />
              <Button
                onClick={handleAddConsideration}
                disabled={!newConsideration.trim()}
                size="sm"
                className="bg-amber-500 hover:bg-amber-600"
              >
                <Plus className="w-4 h-4 mr-1" />
                Agregar
              </Button>
            </div>

            {/* Quick actions */}
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConsiderations(defaultConsiderations)}
                className="text-xs"
              >
                <RefreshCw className="w-3 h-3 mr-1" />
                Restaurar predeterminadas
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConsiderations([])}
                className="text-xs text-red-600 hover:text-red-700"
              >
                <Trash2 className="w-3 h-3 mr-1" />
                Limpiar todas
              </Button>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
};

export { defaultConsiderations };
export default CommercialConsiderationsSection;
